import { useContext, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDPagination from "components/MDPagination";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";

import { useSessionContext } from "context/SessionContext";
import { LanguageContext } from "context";

import { getReward, type RewardItem } from "@shared/api/reward";

function RewardCollection() {
  const { t } = useContext(LanguageContext) as any;

  const [rewards, setRewards] = useState<RewardItem[]>([]);
  const [totalStamps, setTotalStamps] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { session } = useSessionContext();

  // ダイアログ用のステート
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState<RewardItem | null>(null);

  const itemsPerPage = 30;

  useEffect(() => {
    const loadRewards = async () => {
      try {
        if (!session?.access_token) {
          return;
        }
        const data = await getReward(session.access_token);

        setRewards(data.rewards ?? []);
        setTotalStamps(data.totalStamps ?? 0);
      } catch {
        setRewards([]);
        setTotalStamps(0);
      }
    };

    loadRewards();
  }, [session?.access_token]);

  // 1. 獲得済み / 未取得 に分ける
  const unlockedRewards = rewards.filter((r) => r.isUnlocked);
  const lockedRewards = rewards.filter((r) => !r.isUnlocked);

  // 2. 未取得を必要数だけ補完（空のときはダミー30個）
  const notAcquiredDefaults = Array.from({ length: Math.max(30, lockedRewards.length) }, () => ({
    image_url: "/images/no-get.png",
    name: "？？？？",
    isUnlocked: false,
  }));

  const finalLockedRewards = lockedRewards.length
    ? lockedRewards.map((r) => ({
        ...r,
        image_url: "/images/no-get.png",
        name: "？？？？",
        isUnlocked: false,
      }))
    : notAcquiredDefaults;

  // 3. 結合後ページ処理
  const displayRewards = rewards.map((r) => {
    if (!r.isUnlocked) {
      return {
        ...r,
        image_url: "/images/no-get.png", // 未取得時はテキスト画像
        name: "？？？？", // 未取得時の名前
        isUnlocked: false,
      };
    }
    return r;
  });

  const totalPages = Math.ceil(displayRewards.length / itemsPerPage);
  const pagedRewards = displayRewards.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // アイテムクリック時の処理
  const handleRewardClick = (reward: RewardItem) => {
    if (reward.isUnlocked) {
      setSelectedReward(reward);
      setDialogOpen(true);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <Typography variant="h4" mb={2}>
          {t("reward.title")}
        </Typography>

        <Card sx={{ p: 2, minHeight: "calc(100vh - 300px)" }}>
          <Grid container spacing={2}>
            {pagedRewards.map((reward, index) => (
              <Grid item xs={4} sm={2} md={2} key={index}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  onClick={() => handleRewardClick(reward)}
                  sx={{
                    cursor: reward.isUnlocked ? "pointer" : "default",
                    opacity: reward.isUnlocked ? 1 : 0.7,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: reward.isUnlocked ? "scale(1.05)" : "none",
                    },
                  }}
                >
                  <MDAvatar
                    src={reward.image_url}
                    alt={reward.name}
                    size="lg"
                    variant="circular"
                    sx={{
                      width: { xs: 60, sm: 80 },
                      height: { xs: 60, sm: 80 },
                      mb: 0.5,
                      borderRadius: "50%",
                      overflow: "hidden",
                      backgroundColor: "#f5f5f5",
                      border: "none",
                      "& img": {
                        objectFit: reward.isUnlocked ? "cover" : "contain", 
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" textAlign="center" noWrap sx={{ width: "100%" }}>
                    {reward.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="center" mt={3}>
            <MDPagination
              size="small"
              count={totalPages}
              page={currentPage}
              onChange={(_: React.ChangeEvent<unknown>, value: number) => setCurrentPage(value)}
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>

          <Box display="flex" justifyContent="center" mt={3}>
            <Typography variant="body1" fontWeight="bold">
              {t("reward.item")} {totalStamps} {t("reward.piece")}
            </Typography>
          </Box>
        </Card>
      </Box>
      <Footer />

      {/* 詳細ダイアログ表示 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle textAlign="center" sx={{ fontWeight: "bold", color: "info.main" }}>
          {selectedReward?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedReward && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
              <MDAvatar
                src={selectedReward.image_url}
                alt={selectedReward.name}
                variant="circular"
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "none",
                  "& img": {
                    objectFit: "cover", // 枠一杯に
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%", // 丸く切り抜く
                  },
                }}
              />
              <Typography variant="body2" sx={{ mt: 2, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                {selectedReward.detail}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={() => setDialogOpen(false)}>
            {t("StoryCard.close") || "閉じる"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default RewardCollection;